/**
 * @author gabyong.kim
 * @email gabyong.kim@gloovir.com
 * @create date 2023-10-11 11:10:08
 * @modify date 2023-10-11 11:10:08
 * @desc [description]
 */

import { View } from "react-native";
import ForceGraph3D from 'react-force-graph-3d';

import { Fragment, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { ILink, INode } from "./Interface";
import { useDebouncedCallback } from 'use-debounce';
import { PAGE_URL } from "value/Url";
import { useNavigate } from "react-router-dom";
import { SESSIONSTORAGE } from "value/staticValues";



const windowHeight = window.innerHeight;
const windowWidth = window.innerWidth;
let singleClickTimer;
function FocusGraph(
  {sideBarSize, nodes, links, showText, showImportantText, filterDeviceStatus, filterGroup, filterType, filterDataCenter} :
  {sideBarSize:number, nodes:INode[], links:ILink[], showText:boolean, showImportantText:boolean, filterDeviceStatus:string[], filterGroup:string[], filterType:string[], filterDataCenter:string[]}) {
  let ignore = false;
  const fgRef3d:any = useRef();
  const navigate = useNavigate();
  const [height, setHeight] = useState(0);
  const [data, setData] = useState<any>();
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [hoverNode, setHoverNode] = useState(null);
  const [click, setClick] = useState(0);


  function deviceStatusFilter(nodes, links) {
    //filter
    const newNodes = nodes.filter((node:INode)=> !filterDeviceStatus.includes(node.statusCode))
    const newLinks = links.filter((link)=> {
      return newNodes.some((n)=> n.id === ( typeof link.source === "string" ? link.source : link.source.id)) && newNodes.some((n)=> n.id === ( typeof link.target === "string" ? link.source : link.target.id))
    })
    return {nodes:newNodes, links:newLinks}
  }

  function groupFilter(nodes, links) {
    //filter
    const newNodes = nodes.filter((node:INode)=> !filterGroup.includes(node.group))
    const newLinks = links.filter((link)=> {
      return newNodes.some((n)=> n.id === ( typeof link.source === "string" ? link.source : link.source.id)) && newNodes.some((n)=> n.id === ( typeof link.target === "string" ? link.source : link.target.id))
    })
    return {nodes:newNodes, links:newLinks}
  }

  function typeFilter(nodes, links) {
    //filter
    const newNodes = nodes.filter((node:INode)=> !filterType.includes(node.type))
    const newLinks = links.filter((link)=> {
      return newNodes.some((n)=> n.id === ( typeof link.source === "string" ? link.source : link.source.id)) && newNodes.some((n)=> n.id === ( typeof link.target === "string" ? link.source : link.target.id))
    })
    return {nodes:newNodes, links:newLinks}
  }

  function dataCenterFilter(nodes, links) {
    //filter
    const newNodes = nodes.filter((node:INode)=> !filterDataCenter.includes(node.dataCenter))
    const newLinks = links.filter((link)=> {
      return newNodes.some((n)=> n.id === ( typeof link.source === "string" ? link.source : link.source.id)) && newNodes.some((n)=> n.id === ( typeof link.target === "string" ? link.source : link.target.id))
    })
    return {nodes:newNodes, links:newLinks}
  }

  function setFilter() {
    let gData = {nodes, links};

    if(filterDeviceStatus.length > 0) {
      gData=deviceStatusFilter(gData.nodes, gData.links)
    }
    if(filterGroup.length > 0) {
      gData=groupFilter(gData.nodes, gData.links)
    }
    if(filterType.length > 0) {
      gData=typeFilter(gData.nodes, gData.links)
    }
    if(filterDataCenter.length > 0) {
      gData=dataCenterFilter(gData.nodes, gData.links)
    }

    return {nodes:gData.nodes, links:gData.links};

  }

  const addBloom = () => {
    const bloomPass = new UnrealBloomPass();  // 객체에 광원 효과를 주는 함수
    bloomPass.strength = 2;  // 빛의 세기
    bloomPass.radius = 1;     // 빛이 번지는 범위
    bloomPass.threshold = 0;    // 임계값
    fgRef3d.current.postProcessingComposer().addPass(bloomPass)
    fgRef3d.current.d3Force('link').distance(200);
  }

  // [FNXRMS-186] node 네온효과 적용 [ex - https://github.com/vasturiano/react-force-graph/blob/master/example/bloom-effect/index.html]
  useEffect(() => {
    if (!ignore) addBloom()
    return () => { ignore = true }
  },[]);

  useEffect(()=>{
    setHeight(getParentHeight()-60);
  },[])

  useEffect(()=>{
    const {nodes, links} = setFilter();

    // cross-link node objects
    links && links.forEach(link => {
      const sourceNode = nodes.filter(node=>node.id===link.source)[0]
      const targetNode = nodes.filter(node=>node.id===link.target)[0]

      if(!sourceNode && !targetNode) return;

      // node
      if (sourceNode && "neighbors" in sourceNode) {
        sourceNode.neighbors.push(targetNode.id);
      } else {
        const addObject = {neighbors:[]}
        Object.assign(sourceNode, addObject)
      }
      if (targetNode && "neighbors" in targetNode) {
        targetNode.neighbors.push(sourceNode.id);
      } else {
        const addObject = {neighbors:[]}
        Object.assign(targetNode, addObject)
      }

      // link
      if (sourceNode && "links" in sourceNode) {
        sourceNode.links.push(link.source);
      } else {
        const addObject = {links:[]}
        Object.assign(sourceNode, addObject)
      }
      if (targetNode && "links" in targetNode) {
        targetNode.links.push(link.source);
      } else {
        const addObject = {links:[]}
        Object.assign(targetNode, addObject)
      }
    });

    setData({nodes, links});

  }, [filterDeviceStatus, filterGroup, filterType, filterDataCenter])


  // node single click
  // move camera position
  function doSingleClickThing(node) {
    const distance = 500;
    const distRatio = 1 + distance/Math.hypot(node.x, node.y, node.z);

    fgRef3d.current.cameraPosition(
      { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio }, // new position
      node, // lookAt ({ x, y, z })
      2000  // ms transition duration
    );
  }

  // node double click
  function doDoubleClickThing(node) {
    if(node.value < 10) {
      sessionStorage.setItem(SESSIONSTORAGE.VM_TARGET_ID, node.id)
      navigate(`${PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM}/${PAGE_URL.DETAIL_MANAGEMENT}`);
    }
  }

  
  const handleNodeLeftClick = useCallback(node => {
    setClick(click + 1)    
    if (click === 0) {
      singleClickTimer = setTimeout(function() {
        doSingleClickThing(node)
        setClick(0);
      }, 300);
    } else if (click === 1) {
      clearTimeout(singleClickTimer);
      doDoubleClickThing(node)
      setClick(0);
    }
  }, [click]);


  const handleNodeHover = node => {
    highlightNodes.clear();
    highlightLinks.clear();
    if (node) {
      highlightNodes.add(node);
      if(node.neighbors)
        node.neighbors.forEach(neighbor => highlightNodes.add(neighbor));
      if(node.links)
        node.links.forEach(link => highlightLinks.add(link));
    }

    setHoverNode(node || null);
    setHighlightNodes(highlightNodes);
    setHighlightLinks(highlightLinks);
  };

  const handleLinkHover = link => {
    highlightNodes.clear();
    highlightLinks.clear();
    if (link) {
      highlightLinks.add(link);
      highlightNodes.add(link.source);
      highlightNodes.add(link.target);
    }
    setHighlightNodes(highlightNodes);
    setHighlightLinks(highlightLinks);
  };

  function getParentHeight() {
    const parent = document.getElementById('networkTopologyContent') as HTMLElement
    return parent.getBoundingClientRect().height;
  }

  const extraRenderers = [new CSS2DRenderer()];
  
  return (
    <Fragment>
      <ForceGraph3D
          ref={fgRef3d}
          width={windowWidth - sideBarSize - 400}  // windowWidth - margin(left, right) - sideBarWidth - filterWidth
          // height={windowHeight - 260}  // windowHeight - header - topBar
          height={height}
          graphData={data}
          showNavInfo={false}  // 하단 description 표시 여부
          nodeVal={node => node.value}
          nodeLabel="title"
          linkLabel="label"
          backgroundColor="#000003"
          // nodeColor={node=>
          //   node.connectionState === "CONN" ? "#8fde5d" :
          //   node.connectionState === "UNK" ? "#b4bfd0" :
          //   node.connectionState === "DCONN" ? "#ff411a" :
          //   node.connectionState === "FornaX" ? "#c259ed" : "#66bccc"} // 노드 정보에 따라 색상 지정
          nodeAutoColorBy="statusCode"
          nodeOpacity={1}
          nodeResolution={32}
          linkWidth={link => highlightLinks.has(link.id) ? 2 : 1}
          linkDirectionalParticles={2} // link 내 animation
          linkDirectionalParticleWidth={link => highlightLinks.has(link.source.id) ? 2 : 0}
          onNodeHover={handleNodeHover}
          onLinkHover={handleLinkHover}
          nodeThreeObject={node => {
              const nodeEl = document.createElement('div');
              nodeEl.textContent = node.title;
              nodeEl.style.color = "#ffffff"
              nodeEl.style.textShadow = "0 0 2px #000";
              if(node.value > 999) {
                nodeEl.style.fontSize = "24px"
                nodeEl.style.fontWeight = "800"
                nodeEl.style.visibility = showText || showImportantText ? "visible" : "hidden";
              } else if(node.value > 99) {
                nodeEl.style.fontSize = "16px"
                nodeEl.style.fontWeight = "800"
                nodeEl.style.visibility = showText || showImportantText ? "visible" : "hidden";
              } else {
                nodeEl.style.visibility = showText ? "visible" : "hidden";
              }
              nodeEl.className = "node-label"
              return new CSS2DObject(nodeEl);
          }}
          nodeThreeObjectExtend={true}
          controlType={"trackball"}
          extraRenderers={extraRenderers}
          onNodeDragEnd={node => {
            node.fx = node.x;
            node.fy = node.y;
            node.fz = node.z;
          }}
          onNodeClick={handleNodeLeftClick}
        />
    </Fragment>
  )
};
export default function NetworkTopologView(
  {sideBarSize, nodes, links, showText, showImportantText, filterDataCenter, filterDeviceStatus, filterGroup, filterType}:
  {sideBarSize:number, nodes:INode[], links:ILink[], showText:boolean, showImportantText:boolean, filterDataCenter:string[], filterDeviceStatus:string[], filterGroup:string[], filterType:string[]}) {
  return (
    <Fragment>
      {/* Topology */}
      <View style={{height:"100%", alignItems:"center"}}>
        {nodes.length > 0 &&
        <FocusGraph
          sideBarSize={sideBarSize}
          nodes={nodes}
          links={links}
          showText={showText}
          showImportantText={showImportantText}
          filterDeviceStatus={filterDeviceStatus}
          filterGroup={filterGroup}
          filterType={filterType}
          filterDataCenter={filterDataCenter}/>}
      </View>
    </Fragment>
  )
}